import React from 'react';
import { X, Mail, Phone, User, Star } from 'lucide-react';
import { useTranslation } from '../i18n/hooks/useTranslation';
import { ContactPerson } from '../types';

interface PeopleModalProps {
  isOpen: boolean;
  onClose: () => void;
  contactName: string;
  contactPersons: ContactPerson[];
}

export const PeopleModal: React.FC<PeopleModalProps> = ({
  isOpen,
  onClose,
  contactName,
  contactPersons
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-600">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('contact.people')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('contact.peopleFor')} {contactName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {contactPersons && contactPersons.length > 0 ? (
            <div className="space-y-4">
              {contactPersons.map((person, index) => (
                <div
                  key={index}
                  className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <User className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          {person.firstName} {person.lastName}
                        </h3>
                        {person.isPrimary && (
                          <Star className="w-4 h-4 text-warning-500 fill-yellow-500" />
                        )}
                      </div>
                      
                      {person.email && (
                        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400 mb-1">
                          <Mail className="w-4 h-4" />
                          <a
                            href={`mailto:${person.email}`}
                            className="hover:text-primary-600 dark:hover:text-primary-400"
                          >
                            {person.email}
                          </a>
                        </div>
                      )}
                      
                      {person.phone && (
                        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                          <Phone className="w-4 h-4" />
                          <a
                            href={`tel:${person.phone}`}
                            className="hover:text-primary-600 dark:hover:text-primary-400"
                          >
                            {person.phone}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <User className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">
                {t('contact.noPeople')}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-600 p-6">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

